import { FC, MouseEvent, PropsWithChildren, useState } from "react";
import { Score, TeamReference } from "@/models/pdfData";
import { Box, Typography } from "@mui/material";
import { UpdateActionWithState } from "@/models/socketIo/updateAction";
import { gameState } from "@/services/gameState";
import {
  eventIconMap,
  eventSecondaryMap,
  eventTextMap,
} from "./event-history-utils";
import ActionScore from "./ActionScore";
import { useActionBookmarksContext } from "@context/ActionBookmarksContext";
import { millisToTimeRemaining } from "@/services/dateTimeService";
import { useGameDataContext } from "@context/GameDataContext";
import { LiveTv } from "@mui/icons-material";
import Chronic from "../custom-icons/Chronic";
import EventCardMenu, { ContextMenuPosition } from "./EventCardMenu";
import { getScoringTeamFromChallenge } from "@/utils/challenge-utils";
import { getScoringTeamFromAction } from "@/utils/action-history";

const getTeamFromAction = (
  action: UpdateActionWithState,
): TeamReference | undefined => {
  if ("payload" in action) {
    const payload = action.payload as any;
    if (payload === "team1" || payload === "team2") {
      return payload;
    }
    if (payload && typeof payload === "object" && "teamCode" in payload) {
      return payload.teamCode as TeamReference;
    }
  }
};

const getScoreFromAction = (action: UpdateActionWithState) => {
  if (
    action.type === "[Challenges] Challenge" ||
    action.type === "[Challenges] Challenge Declined" ||
    action.type === "[Challenges] Challenge unsuccessful" ||
    action.type === "[Challenges] Challenge Unresolved"
  ) {
    return action.payload.setScoreBeforeChallenge;
  } else if ("setScore" in action) {
    return action.score;
  } else {
    return undefined;
  }
};

const EventCard: FC<{
  action: UpdateActionWithState;
  variant: "wide" | "narrow";
  showDurationSinceGameStart: boolean;
  bookmarksFilterActive: boolean;
}> = ({
  action,
  variant,
  showDurationSinceGameStart,
  bookmarksFilterActive,
}) => {
  const { addActionBookmark, removeActionBookmark, actionBookmarks } =
    useActionBookmarksContext();
  const { game } = useGameDataContext();
  const firstScoreTime = gameState("timeFirstScoreInMatch");
  const startTime = gameState("startTime");
  const teams = gameState("teams");
  const playedSets = gameState("playedSets");

  const isFavorite = actionBookmarks?.includes(action.orderRank);

  const [contextMenu, setContextMenu] = useState<ContextMenuPosition>();

  const openContextMenu = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setContextMenu(
      !contextMenu
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : undefined,
    );
  };

  const closeContextMenu = () => {
    setContextMenu(undefined);
  };

  const teamCode = getScoringTeamFromAction(action);

  const score = getScoreFromAction(action);

  const setNumber =
    "payload" in action &&
    typeof action.payload === "object" &&
    "setNumber" in (action.payload as { setNumber: number })
      ? (action.payload as { setNumber: number }).setNumber
      : action.setNumber;

  return (
    <Box
      key={action.orderRank}
      display={"flex"}
      gap={variant === "narrow" ? 0 : "2em"}
      width={"100%"}
      flexDirection={variant === "narrow" ? "column" : "row"}
      onClick={() => {
        if (!bookmarksFilterActive && !contextMenu) {
          if (isFavorite) {
            removeActionBookmark(action.orderRank);
          } else {
            addActionBookmark(action.orderRank);
          }
        }
      }}
      onContextMenu={openContextMenu}
    >
      <EventCardMenu
        contextMenu={contextMenu}
        closeContextMenu={closeContextMenu}
        action={action}
      />
      <Box
        display={"flex"}
        alignItems={"flex-start"}
        flexDirection={"column"}
        justifyContent={"center"}
        borderLeft={isFavorite ? "4px solid #46b802" : "4px solid transparent"}
        paddingLeft={"4px"}
      >
        {score && (
          <ActionScore
            score={score}
            teams={teams}
            variant={variant}
            team={teamCode}
            setNumber={setNumber}
            playedSets={playedSets}
            action={action}
          />
        )}
        {showDurationSinceGameStart && (
          <Typography
            variant="caption"
            color="text.secondary"
            display={"flex"}
            alignItems={"baseline"}
            gap={"0.25rem"}
          >
            <Chronic sx={{ fontSize: "0.75rem" }} />
            {`${action.timestamp < startTime ? "-" : ""}${millisToTimeRemaining(Math.abs(action.timestamp - startTime))}`}
          </Typography>
        )}
        {showDurationSinceGameStart &&
          game?.gameData.gameStartVideoOffset &&
          action.timestamp -
            firstScoreTime +
            game.gameData.gameStartVideoOffset >
            0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              display={"flex"}
              alignItems={"baseline"}
              gap={"0.25rem"}
            >
              <LiveTv sx={{ fontSize: "0.75rem" }} />
              {millisToTimeRemaining(
                Math.abs(
                  action.timestamp -
                    firstScoreTime +
                    game.gameData.gameStartVideoOffset,
                ),
              )}
            </Typography>
          )}
      </Box>
      <RenderEventMarkup action={action} variant={variant} />
    </Box>
  );
};

const RenderEventDetail: FC<{
  action: UpdateActionWithState;
  variant: "wide" | "narrow";
}> = ({ action, variant }) => {
  const secondary = eventSecondaryMap({ action });
  return (
    <RenderEventDetailMarkup variant={variant} columns={!!secondary ? 2 : 1}>
      <Typography fontSize={variant === "narrow" ? "1em" : "1.2em"}>
        {eventTextMap(action)}
      </Typography>
      {secondary}
    </RenderEventDetailMarkup>
  );
};

const RenderEventDetailMarkup: FC<
  PropsWithChildren<{
    variant: "wide" | "narrow";
    columns?: number;
  }>
> = ({ children, variant, columns = 2 }) => {
  return (
    <Box
      display={"grid"}
      gap={variant === "narrow" ? 0 : 2}
      justifyContent={"flex-start"}
      alignItems={"center"}
      sx={{
        ...(variant === "narrow"
          ? { gridTemplateRows: `repeat(${columns}, auto)` }
          : {
              gridTemplateColumns: `repeat(${columns - 1}, minmax(min-content, 1fr)) 1fr`,
            }),
      }}
    >
      {children}
    </Box>
  );
};

export const RenderEventMarkup: FC<{
  action: UpdateActionWithState;
  variant: "wide" | "narrow";
}> = ({ action, variant }) => {
  return (
    <Box
      display={"grid"}
      gridTemplateColumns={"auto 1fr 1fr"}
      gap={2}
      alignItems={"center"}
      justifyContent={"center"}
      width={"100%"}
    >
      <Box sx={{ gridColumnStart: 1, gridColumnEnd: 2, display: "flex" }}>
        {eventIconMap(action)}
      </Box>
      <Box
        sx={{ gridColumnStart: 2, gridColumnEnd: 4, display: "inline-grid" }}
      >
        <RenderEventDetail action={action} variant={variant} />
      </Box>
    </Box>
  );
};

export default EventCard;
